import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Play, Pause, RotateCcw, X } from 'lucide-react';
interface PomodoroTimerProps {
  onClose: () => void;
}
export function PomodoroTimer({
  onClose
}: PomodoroTimerProps) {
  const TOTAL_TIME = 24 * 60; // 24 minutes in seconds
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [isRunning, setIsRunning] = useState(false);
  const [task, setTask] = useState('');
  const [session, setSession] = useState(2);
  const totalSessions = 4;
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsRunning(false);
    }
    return () => clearInterval(interval);
  }, [isRunning, timeLeft]);
  const toggleTimer = () => setIsRunning(!isRunning);
  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(TOTAL_TIME);
  };
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  const progress = 1 - timeLeft / TOTAL_TIME;
  const radius = 150;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - progress * circumference;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.98
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.98
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className="min-h-screen w-full bg-app-bg flex flex-col" data-id="element-36">
      {/* Top Bar */}
      <header className="flex items-center justify-between p-6 max-w-4xl w-full mx-auto" data-id="element-37">
        <button onClick={onClose} className="flex items-center gap-2 text-neutral-400 hover:text-neutral-100 transition-colors group" data-id="element-38">
          <div className="w-8 h-8 rounded-full bg-app-surface border border-app-border flex items-center justify-center group-hover:border-neutral-700 transition-colors" data-id="element-39">
            <ChevronLeft size={18} data-id="element-40" />
          </div>
          <span className="font-medium text-sm" data-id="element-41">All tools</span>
        </button>

        <div className="flex items-center gap-2 font-medium text-neutral-200" data-id="element-42">
          <span className="text-xl" data-id="element-43">🍅</span>
          <span data-id="element-44">Pomodoro Timer</span>
        </div>

        <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 hover:text-neutral-200 hover:bg-app-surface transition-colors" data-id="element-45">
          <X size={20} data-id="element-46" />
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6 max-w-2xl w-full mx-auto -mt-12" data-id="element-47">
        {/* Task Input */}
        <div className="w-full max-w-md mb-12" data-id="element-48">
          <input type="text" value={task} onChange={e => setTask(e.target.value)} placeholder="What are you focusing on?" className="w-full bg-transparent border-b-2 border-app-border focus:border-app-accent text-center text-xl md:text-2xl py-3 outline-none text-neutral-100 placeholder:text-neutral-600 transition-colors" data-id="element-49" />
        </div>

        {/* Timer Circle */}
        <div className="relative w-[320px] h-[320px] flex items-center justify-center mb-12" data-id="element-50">
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 340 340" data-id="element-51">
            {/* Background Ring */}
            <circle cx="170" cy="170" r={radius} fill="none" stroke="#1f1f22" strokeWidth="6" data-id="element-52" />
            {/* Progress Ring */}
            <motion.circle cx="170" cy="170" r={radius} fill="none" stroke="#fb7185" strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} animate={{
            strokeDashoffset
          }} transition={{
            duration: 0.5,
            ease: 'linear'
          }} data-id="element-53" />
          </svg>

          <div className="flex flex-col items-center z-10" data-id="element-54">
            <span className="text-7xl md:text-8xl font-extralight tracking-tight tabular-nums text-neutral-100" data-id="element-55">
              {formattedTime}
            </span>
            <span className="text-neutral-500 font-medium mt-2 tracking-wide uppercase text-sm" data-id="element-56">
              Focus Session
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-6 mb-12" data-id="element-57">
          <button onClick={resetTimer} className="w-12 h-12 rounded-full flex items-center justify-center text-neutral-400 hover:text-neutral-100 hover:bg-app-surface border border-transparent hover:border-app-border transition-all" aria-label="Reset timer" data-id="element-58">
            <RotateCcw size={20} data-id="element-59" />
          </button>
          <button onClick={toggleTimer} className="w-20 h-20 rounded-full bg-app-accent text-app-bg flex items-center justify-center hover:bg-rose-300 hover:scale-105 active:scale-95 transition-all shadow-[0_0_30px_rgba(251,113,133,0.3)]" aria-label={isRunning ? 'Pause timer' : 'Start timer'} data-id="element-60">
            {isRunning ? <Pause size={32} fill="currentColor" data-id="element-61" /> : <Play size={32} fill="currentColor" className="ml-1" data-id="element-62" />}
          </button>
          <div className="w-12 h-12" data-id="element-63" />{' '}
          {/* Spacer to balance the reset button */}
        </div>

        {/* Session Indicator */}
        <div className="flex flex-col items-center gap-3" data-id="element-64">
          <div className="flex gap-2" data-id="element-65">
            {Array.from({
            length: totalSessions
          }).map((_, i) => <div key={i} className={`w-2.5 h-2.5 rounded-full transition-colors ${i < session ? 'bg-app-accent' : 'bg-app-surface border border-app-border'}`} data-id="element-66" />)}
          </div>
          <span className="text-sm text-neutral-500 font-medium" data-id="element-67">
            Session {session} of {totalSessions}
          </span>
        </div>
      </main>
    </motion.div>;
}